import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-bits";
import { Package, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/i18n";

const STATUS_CLASSES = {
  pending: "text-amber-300 border-amber-300/30",
  paid: "text-emerald-300 border-emerald-300/30",
  shipped: "text-blue-300 border-blue-300/30",
  delivered: "text-gold border-gold/30",
  cancelled: "text-red-400 border-red-400/30",
};

export const StatusBadge = ({ status }) => {
  const { t } = useLang();
  const cls = STATUS_CLASSES[status] || "text-mist border-cream/20";
  return <span data-testid="order-status-badge" className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-3 py-1 ${cls}`}>{t(`status.${status}`, status)}</span>;
};

export const AccountOrders = () => {
  const { t, lang } = useLang();
  const locale = lang === "fr" ? "fr-FR" : "nl-NL";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((d) => setOrders(d.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-32 pb-24">
      <PageHeader eyebrow={t("account.ordersEyebrow")} title={<>{t("account.profile")} <span className="italic normal-case text-gold">{t("account.ordersTitle2")}</span></>} />
      {loading ? (
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 bg-ink animate-pulse border border-gold/5" />)}</div>
      ) : orders.length === 0 ? (
        <div data-testid="orders-empty-state" className="border border-gold/15 bg-ink px-8 py-20 text-center">
          <Package size={28} className="text-gold/60 mx-auto mb-6" />
          <p className="font-serif text-2xl text-cream">{t("account.noOrders")}</p>
          <Link data-testid="orders-empty-shop" to="/winkel" className="inline-block mt-8 bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-3.5">{t("account.toShop")}</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <Link key={o.order_id} to={`/account/bestellingen/${o.order_id}`} data-testid={`order-row-${o.order_id}`} className="flex flex-wrap items-center justify-between gap-4 border border-gold/10 bg-ink p-6 hover:border-gold/40 transition-colors group">
              <div>
                <p className="font-mono text-xs tracking-[0.2em] text-gold">{o.order_id}</p>
                <p className="font-mono text-[10px] text-smoke mt-1.5">{new Date(o.created_at).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" })} · {o.items.length} {t("account.articles")}</p>
              </div>
              <div className="flex items-center gap-6">
                <span className="font-serif text-xl text-cream">€ {o.total?.toFixed(2).replace(".", ",")}</span>
                <StatusBadge status={o.status} />
                <ChevronRight size={16} className="text-smoke group-hover:text-gold transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountOrders;
